import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Box,
  Card,
  CardContent,
} from '@mui/material';
import DictationTextField from './DictationTextField';
import { reflectionsApi } from '../api/services';

interface GuidedReflectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  date?: string;
  trigger?: string;
}

const REFLECTION_PROMPTS = [
  { section: 'Wins', prompt: 'What went well today? What are you proud of?', icon: '\u{1F3C6}' },
  { section: 'Challenges', prompt: 'What was difficult? What didn\'t go as planned?', icon: '\u{26A1}' },
  { section: 'Learnings', prompt: 'What did you learn? What would you do differently?', icon: '\u{1F4A1}' },
  { section: 'Energy', prompt: 'How is your energy? What drained or energised you?', icon: '\u{1F50B}' },
  { section: 'Tomorrow', prompt: 'What\'s the one thing you want to accomplish tomorrow?', icon: '\u{1F3AF}' },
];

function formatToday(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function compileContent(answers: string[]): string {
  return REFLECTION_PROMPTS
    .map((p, i) => ({ heading: p.section, icon: p.icon, text: answers[i] }))
    .filter((s) => s.text.trim())
    .map((s) => `## ${s.icon} ${s.heading}\n${s.text.trim()}`)
    .join('\n\n');
}

export default function GuidedReflectionDialog({
  open,
  onClose,
  onSaved,
  date,
  trigger = 'Manual',
}: GuidedReflectionDialogProps) {
  const [answers, setAnswers] = useState<string[]>(REFLECTION_PROMPTS.map(() => ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleAnswerChange = useCallback(
    (index: number) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setAnswers((prev) => {
        const next = [...prev];
        next[index] = event.target.value;
        return next;
      });
    },
    [],
  );

  const handleAnswerTranscript = useCallback(
    (index: number) => (text: string) => {
      setAnswers((prev) => {
        const next = [...prev];
        next[index] = text;
        return next;
      });
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    setError(null);

    const finalContent = compileContent(answers);
    if (!finalContent.trim()) {
      setError('Please fill in at least one section before saving.');
      setSaving(false);
      return;
    }

    // Extract insights from the Learnings section and adjustments from Tomorrow
    const insights = answers[2].trim() || undefined;
    const adjustmentsMade = answers[4].trim() || undefined;
    const reflectionDate = date || formatToday();

    try {
      await reflectionsApi.create({
        date: reflectionDate,
        trigger,
        content: finalContent,
        insights,
        adjustmentsMade,
      });
      setSnackbarOpen(true);
      onSaved();
      handleReset();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to save reflection. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }, [answers, date, trigger, onSaved]);

  const handleReset = useCallback(() => {
    setAnswers(REFLECTION_PROMPTS.map(() => ''));
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    // Delay reset so the dialog animation completes before clearing state
    setTimeout(handleReset, 300);
  }, [onClose, handleReset]);

  const hasAnyContent = answers.some((a) => a.trim().length > 0);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" component="span" fontWeight={700}>
            Guided Reflection
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Take a few minutes to reflect on your day. Fill in as many or as few sections as you like.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {REFLECTION_PROMPTS.map((prompt, index) => (
              <Card
                key={prompt.section}
                variant="outlined"
                sx={{
                  borderColor: answers[index].trim() ? 'primary.main' : 'divider',
                  transition: 'border-color 0.2s',
                }}
              >
                <CardContent sx={{ pb: '16px !important' }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="h5" component="span" sx={{ lineHeight: 1 }}>
                      {prompt.icon}
                    </Typography>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {prompt.section}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {prompt.prompt}
                      </Typography>
                    </Box>
                  </Stack>
                  <DictationTextField
                    multiline
                    rows={3}
                    fullWidth
                    placeholder={`Your thoughts on ${prompt.section.toLowerCase()}...`}
                    value={answers[index]}
                    onChange={handleAnswerChange(index)}
                    onTranscriptChange={handleAnswerTranscript(index)}
                    size="small"
                  />
                </CardContent>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !hasAnyContent}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Saving...' : 'Save Reflection'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message="Reflection saved successfully"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
